import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Facebook, Instagram } from "lucide-react";
import axios from "axios";
import useSiteInfo, { hoursLines, telHref } from "@/hooks/useSiteInfo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const site = useSiteInfo();
  const [features, setFeatures] = useState({
    feature_fahrzeuge: true,
    feature_aktionen: true,
    feature_termine: true,
    feature_bewertungen: true,
    feature_kontakt: true,
    feature_reifenlager: true,
    feature_kundenportal: true
  });

  useEffect(() => {
    const loadFeatures = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        setFeatures({
          feature_fahrzeuge: res.data.feature_fahrzeuge ?? true,
          feature_aktionen: res.data.feature_aktionen ?? true,
          feature_termine: res.data.feature_termine ?? true,
          feature_bewertungen: res.data.feature_bewertungen ?? true,
          feature_kontakt: res.data.feature_kontakt ?? true,
          feature_reifenlager: res.data.feature_reifenlager ?? true,
          feature_kundenportal: res.data.feature_kundenportal ?? true
        });
      } catch (error) {
        console.error("Error loading features:", error);
      }
    };
    loadFeatures();
  }, []);

  return (
    <footer className="bg-black text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#EFDF00]  flex items-center justify-center">
                <span className="text-black font-bold text-xl">AT</span>
              </div>
              <div>
                <h3 className="text-lg font-bold tracking-tight">
                  AUTO THURM
                </h3>
                <p className="text-xs text-[#888B8D]">Renault & Dacia</p>
              </div>
            </div>
            <p className="text-[#D9D9D6] text-sm leading-relaxed mb-6">
              Ihr kompetenter Partner für Renault und Dacia in der Region. 
              Wir bieten erstklassigen Service und eine große Auswahl an Neu- und Gebrauchtwagen.
            </p>
            <div className="flex gap-4">
              <a 
                href="#" 
                className="w-10 h-10 bg-[#333333]  flex items-center justify-center hover:bg-[#EFDF00] hover:text-black transition-colors"
                aria-label="Facebook"
                data-testid="footer-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 bg-[#333333]  flex items-center justify-center hover:bg-[#EFDF00] hover:text-black transition-colors"
                aria-label="Instagram"
                data-testid="footer-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-6">Schnellzugriff</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="footer-link text-sm text-[#D9D9D6] hover:text-[#EFDF00] transition-colors">
                  Startseite
                </Link>
              </li>
              {features.feature_fahrzeuge && (
              <li>
                <Link to="/fahrzeuge" className="footer-link text-sm text-[#D9D9D6] hover:text-[#EFDF00] transition-colors">
                  Fahrzeuge
                </Link>
              </li>
              )}
              {features.feature_aktionen && (
              <li>
                <Link to="/aktionen" className="footer-link text-sm text-[#D9D9D6] hover:text-[#EFDF00] transition-colors">
                  Aktionen
                </Link>
              </li>
              )}
              {features.feature_termine && (
              <li>
                <Link to="/termin" className="footer-link text-sm hover:text-[#EFDF00] transition-colors">
                  Termin buchen
                </Link>
              </li>
              )}
              {features.feature_bewertungen && (
              <li>
                <Link to="/bewertungen" className="footer-link text-sm hover:text-[#EFDF00] transition-colors">
                  Bewertungen
                </Link>
              </li>
              )}
              <li>
                <Link to="/kontakt" className="footer-link text-sm hover:text-[#EFDF00] transition-colors">
                  Kontakt
                </Link>
              </li>
              {features.feature_kundenportal && (
              <li>
                <Link to="/login" className="footer-link text-sm hover:text-[#EFDF00] transition-colors">
                  Kundenportal
                </Link>
              </li>
              )}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-bold  mb-6">Werkstatt-Services</h4>
            <ul className="space-y-3 text-#888B8D text-sm">
              <li>Inspektion & Wartung</li>
              <li>Reparaturen aller Art</li>
              <li>TÜV & HU Abnahme</li>
              <li>Klimaanlagen-Service</li>
              {features.feature_reifenlager && <li>Reifenservice & Einlagerung</li>}
              <li>Unfallinstandsetzung</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold  mb-6">Kontakt</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#EFDF00] flex-shrink-0 mt-0.5" />
                <span className="text-#888B8D text-sm">
                  {site.company_street}<br />
                  {site.company_city}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#EFDF00] flex-shrink-0" />
                <a href={telHref(site.company_phone)} className="text-#888B8D text-sm hover:text-[#EFDF00] transition-colors" data-testid="footer-phone">
                  {site.company_phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#EFDF00] flex-shrink-0" />
                <a href={`mailto:${site.company_email}`} className="text-#888B8D text-sm hover:text-[#EFDF00] transition-colors" data-testid="footer-email">
                  {site.company_email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#EFDF00] flex-shrink-0 mt-0.5" />
                <div className="text-[#D9D9D6] text-sm" data-testid="footer-hours">
                  {hoursLines(site).map((l) => <p key={l}>{l}</p>)}
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-#1a1a1a">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-#888B8D text-sm">
              © {new Date().getFullYear()} {site.company_name}. Alle Rechte vorbehalten.
            </p>
            <div className="flex gap-6">
              <a href="/impressum" className="text-#888B8D text-sm hover:text-[#D9D9D6] transition-colors">
                Impressum
              </a>
              <a href="/datenschutz" className="text-#888B8D text-sm hover:text-[#D9D9D6] transition-colors">
                Datenschutz
              </a>
              <a href="/agb" className="text-#888B8D text-sm hover:text-[#D9D9D6] transition-colors">
                AGB
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
